import React, { useEffect, useState } from 'react';
import Title from 'Components/ComponentLibrary/Atoms/Text/Title';
import { usePageContentContext } from 'Views/Page/Contexts/PageContentContext';
import Subtitle from 'Components/ComponentLibrary/Atoms/Text/Subtitle';
import fr from 'facet-rest';
import usePlaidLink from 'Hooks/usePlaidLink';
import { DisabledOverlay } from 'Views/Page/Content/Modal/Layouts/ApexFundingAccountListStep';
import PlaidButton from 'Components/UI/PlaidButton';
import {
  FDBox, FDContentContainer, FDPaper, FDSecondaryActionButton, FDStack,
} from '@facet-wealth/design';
import Text from 'Components/ComponentLibrary/Atoms/Text';
import { useSnackbarContext } from 'Context/SnackbarContext';
import FundingInstructionsCollapsibleCardContainer
  from 'Components/Modals/AccountFundingInstructions/FundingInstructionsCollapsibleCard/FundingInstructionsCollapsibleCardContainer';
import useEventTrackingLogEvent, { categories, eventTypes } from 'EventTracking/useEventTrackingLogEvent';
import { useModalContext } from 'Views/Page/Contexts/ModalContext';
import { usePlaidStart } from 'Hooks/useRefreshEvent';
import ApexFundingAccountResourceSummaryCard from './ApexFundingAccountResourceSummaryCard';

export default function ACHTransferSelectAccountTemplate() {
  const { displayErrorMessage, displaySuccess } = useSnackbarContext();
  const { logStandardizedEvent } = useEventTrackingLogEvent();
  const { loadModalCollectionByLink } = useModalContext();

  const { pageContentResource: resource, loadPageContentLink, refreshPageContentCollection } = usePageContentContext();
  const linkedAccounts = resource.attr('linkedAccounts').resources().head();
  const totalLinkedAccounts = linkedAccounts.attr('items').resources().length();
  const availableAccounts = resource.attr('availableAccounts').resources().head();
  const [busy, setBusy] = useState(false);
  const [activePlaidTokenURL, setActivePlaidTokenURL] = useState(null);

  const clearActivePlaidTokenURL = () => setActivePlaidTokenURL(null);
  const [isPlaidFlowActive, setIsPlaidFlowActive] = useState(false);
  const handlePlaidFlowStart = () => setIsPlaidFlowActive(true);

  const handlePlaidFlowStop = () => {
    clearActivePlaidTokenURL();
    setIsPlaidFlowActive(false);
  };

  const handlePlaidFailure = (e) => {
    displayErrorMessage(e, e.errors[0]?.detail || 'Unexpected error occurred');
    handlePlaidFlowStop();
  };

  usePlaidStart(() => {
    handlePlaidFlowStart();
  }, []);

  async function linkApexFundingAccount(apexFundingAccountLink) {
    if (totalLinkedAccounts >= 2) return null;

    try {
      const [linkedAccount] = await apexFundingAccountLink.post({}, { deprecatedAllowAlterOfRelation: true });
      return linkedAccount;
    } catch (e) {
      displayErrorMessage(e, e.errors[0]?.detail || 'Unable to connect the account');
      handlePlaidFlowStop();
      await refreshPageContentCollection();
      return null;
    }
  }

  async function initiateApexFundingAccountLinking(apexFundingAccountLink) {
    const linkedApexFundingAccount = await linkApexFundingAccount(apexFundingAccountLink);

    if (linkedApexFundingAccount) {
      await loadPageContentLink(linkedApexFundingAccount.relationship(fr.relationships.apexFundingAccountLinking.name).link('related'));
    }
  }

  async function handlePlaidSuccess(exchangeTokenResp) {
    displaySuccess('Account(s) synced successfully');
    await refreshPageContentCollection();

    // fetch apex funding account
    const plaidLinkTokenResource = exchangeTokenResp.resources().head();
    const apexFundingAccounts = await plaidLinkTokenResource.withRelationship(
      fr.relationships.linkedApexFundingAccounts.name,
      async (rel) => rel.link('related').get(),
      () => null,
    );

    // if we didnt fetch any apex funding accounts, then we just did a refresh.
    // we now just need to remove the mask.
    if (!apexFundingAccounts) {
      handlePlaidFlowStop();
      return;
    }

    // were more than 1 account linked? if so, dont link to managed account
    if (apexFundingAccounts.resources().length() > 1) {
      handlePlaidFlowStop();
      return;
    }

    const [apexFundingAccount] = apexFundingAccounts;

    // check if we have plaid token relationship. this means this is pending micro-deposits
    const isPendingMicroDeposits = apexFundingAccount.withRelationship(
      fr.relationships.plaidToken.name,
      () => {
        return true;
      },
      () => {
        return false;
      },
    );

    if (isPendingMicroDeposits) {
      // plaid overlay no more needed
      handlePlaidFlowStop();
      return;
    }

    // determine linked account
    let linkedApexFundingAccount;
    if (apexFundingAccount.attr('isLinked') === 'true') {
      linkedApexFundingAccount = apexFundingAccount;
    } else {
      linkedApexFundingAccount = await linkApexFundingAccount(apexFundingAccount.link('self'));
      await refreshPageContentCollection();
    }

    // plaid overlay no more needed
    handlePlaidFlowStop();

    // if account is linked, continue with linking process
    if (linkedApexFundingAccount) {
      loadPageContentLink(linkedApexFundingAccount.relationship(fr.relationships.apexFundingAccountLinking.name).link('related'));
    }
  }

  const handleAddAccountOnClick = async (onClick) => {
    logStandardizedEvent(categories.accountFunding, eventTypes.clickButton, 'add bank account for funding clicked');
    await onClick();
  };

  // plaid micro-deposit stuff
  const { handleClick: plaidLinkHandleClick, token: plaidToken } = usePlaidLink({
    onSuccess: handlePlaidSuccess,
    onExit: handlePlaidFlowStop,
    onLoad: handlePlaidFlowStart,
    onFailure: handlePlaidFailure,
    plaidTokenLink: activePlaidTokenURL,
  });

  useEffect(() => {
    if (activePlaidTokenURL) {
      plaidLinkHandleClick();
    }
  }, [plaidToken]);

  const handleClickForAccount = async (action) => {
    if (busy) return;

    try {
      setBusy(true);
      if (action.name === 'transfer') {
        await loadPageContentLink(action.link);
        return;
      }

      if (action.name === 'link') {
        await initiateApexFundingAccountLinking(action.link);
        return;
      }

      if (['sync', 'verify'].includes(action.name)) {
        await loadPageContentLink(action.link);
        return;
      }

      if (action.name === 'plaidToken') {
        setActivePlaidTokenURL(action.link.url());
        return;
      }

      if (['relink'].includes(action.name)) {
        loadModalCollectionByLink(action.link.url());
      }
    } catch (e) {
      displayErrorMessage(e, e.errors[0]?.detail || 'Unable to perform the action');
    } finally {
      setBusy(false);
    }
  };

  const handleLinkFundingAccount = async (args) => {
    logStandardizedEvent(categories.accountFunding, eventTypes.clickButton, 'available funding account clicked for link', {
      eventProperties: {
        id: args.resource.id(),
        name: args.resource.attr('title'),
      },
    });

    await handleClickForAccount(args.action);
  };

  const handleSelectAccountForTransfer = async (args) => {
    logStandardizedEvent(categories.accountFunding, eventTypes.clickButton, 'funding account clicked to fund', {
      eventProperties: {
        id: args.resource.id(),
        name: args.resource.attr('title'),
      },
    });

    await handleClickForAccount(args.action);
  };

  const handleClickForAccountAction = async ({ action }) => {
    if (busy) return;

    try {
      setBusy(true);
      await loadPageContentLink(action.link);
    } catch (e) {
      displayErrorMessage(e, e.errors[0]?.detail || 'Unable to perform the action');
    } finally {
      setBusy(false);
    }
  };

  const canLinkMoreAccounts = totalLinkedAccounts < 2;

  const fundingInstructionsRelationship = resource.relationship(fr.relationships.fundingInstructions.name)?.link('related');
  return <>
    <FDBox margin={400}>
      <FDContentContainer maxWidth={'sm'}>
        <FDStack direction={'column'} alignItems={'center'} spacing={3} justifyContent="flex-start">
          <Title text={resource.attr('title')} test="wizard-step-title" />
          <Subtitle text={resource.attr('subtitle')} test="wizard-step-subtitle" />
          {fundingInstructionsRelationship && (
          <FundingInstructionsCollapsibleCardContainer
            defaultExpanded
            title={'Additional Instructions'}
            fundingInstructionsLink={fundingInstructionsRelationship}
          />
          )}
          <DisabledOverlay disabled={isPlaidFlowActive}>
            <FDStack direction={'column'} spacing={4} alignItems="flex-start" justifyContent="flex-start">
              <FDBox width={'100%'}>
                <FDPaper padding={400}>
                  <FDStack direction={'column'} spacing={2} alignItems="flex-start" justifyContent="flex-start">
                    <FDBox width={'100%'}>
                      <FDStack justifyContent={'space-between'} alignItems={'center'}>
                        <Text weight="medium" variant="h6" color="textPrimary" value={linkedAccounts.attr('title')} />
                        <Text weight="medium" variant="h6" color="textPrimary" value={`${totalLinkedAccounts}/2`} />
                      </FDStack>
                    </FDBox>

                    <FDBox width={'100%'}>
                      <FDStack direction={'column'} spacing={2} alignItems="flex-start" justifyContent="flex-start">
                        {linkedAccounts.attr('items').resources().list().map(account => (<ApexFundingAccountResourceSummaryCard
                          key={account.id()}
                          resource={account}
                          onClick={handleSelectAccountForTransfer}
                          onActionClick={handleClickForAccountAction}
                        />))}
                        {canLinkMoreAccounts && (
                          <FDBox width={'100%'} key={`placeholder=-${0}`}>
                            <FDPaper>
                              <FDBox padding={400}>
                                <Text color={'textSecondary'} value={'Select an account from the list below'} />
                              </FDBox>
                            </FDPaper>
                          </FDBox>
                        )}
                      </FDStack>
                    </FDBox>
                  </FDStack>
                </FDPaper>
              </FDBox>

              <FDBox width={'100%'}>
                <FDStack direction={'column'} spacing={2} alignItems="flex-start" justifyContent="flex-start">
                  <FDBox width={'100%'}>
                    <FDStack justifyContent={'space-between'} alignItems={'center'}>
                      <Text weight="medium" variant="h6" color="textPrimary" value={availableAccounts.attr('title')} />
                      <PlaidButton
                        color="secondary"
                        onSuccess={handlePlaidSuccess}
                        onExit={handlePlaidFlowStop}
                        onLoad={handlePlaidFlowStart}
                        onFailure={handlePlaidFailure}
                        component={({ onClick, disabled }) => (
                          <FDSecondaryActionButton
                            size={'sm'}
                            icon={'add'}
                            label={'Add Account'}
                            onClick={() => handleAddAccountOnClick(onClick)}
                            disabled={disabled || isPlaidFlowActive}
                            test="ach-transfer-add-account"
                          />)}
                        plaidTokenLink={resource.relationship(fr.relationships.plaidToken.name).link('related').url()}
                      />
                    </FDStack>
                  </FDBox>

                  <FDBox width={'100%'}>
                    <FDStack direction={'column'} spacing={1} alignItems="flex-start" justifyContent="flex-start">
                      {availableAccounts.attr('items').resources().list().map(account => (<ApexFundingAccountResourceSummaryCard
                        key={account.id()}
                        resource={account}
                        onClick={handleLinkFundingAccount}
                        onActionClick={handleClickForAccountAction}
                      />))}
                    </FDStack>
                  </FDBox>
                </FDStack>
              </FDBox>
            </FDStack>
          </DisabledOverlay>
        </FDStack>
      </FDContentContainer>
    </FDBox>
  </>;
}
